import { usePagination } from '@uhg-abyss/web/hooks/usePagination';
import { styled } from '@uhg-abyss/web/tools/styled';
import { Button } from '@uhg-abyss/web/ui/Button';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { StepIndicator } from '@uhg-abyss/web/ui/StepIndicator';
import React, { useEffect } from 'react';
import { useForm } from '@uhg-abyss/web/hooks/useForm';
import { FormProvider } from '@uhg-abyss/web/ui/FormProvider';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { config } from '@uhg-abyss/web/tools/config';
import { useSelector } from 'react-redux';
import { event } from '@uhg-abyss/web/tools/event';
import { useDSUserInfo } from '@src/hooks/useDsUserInfo';
import {
  useLeadGenerationApi,
  resetLeadGenerationApi,
} from '@src/hooks/useLeadGenerationApi';
import { FirstPart } from './FirstPart';
import { OrgStepTwo } from './OrgStepTwo';
import { ThirdPart } from './ThirdPart';
// import { ConnectToAPI } from './ConnectToAPI';
import { constants } from '@src/common/Utils/Constants';
import { Completed } from './Completed';
import { IntegrateAPI } from './IntegrateAPI';
import { useUserInfo } from '@src/hooks/useUserInfo';

const StepContent = styled('div', {
  color: `${constants.FONT_COLOR_GREY} !important`,
  fontSize: '16px',
  fontWeight: 400,
  minHeight: '120px',
  letterSpacing: '0px',
  lineHeight: '24px',
  marginBottom: '48px',
  textAlign: 'center',
});

export const IntakeForm = () => {
  const paginationProps = usePagination({ pages: 5, start: 1 });
  const [submitLeadingGenApi, postLeadGenerationApi] = useLeadGenerationApi();
  const [buttonText, setButtonText] = React.useState('Next');
  const form = useForm({ mode: 'onChange' });
  const jobName = 'request a meeting with an api consultant';
  const userDSInfo = useSelector((state) => {
    return state.GET_DS_USER_INFO.data;
  });
  const [dsUserInfo, getDSUserInfo] = useDSUserInfo();
  const userInfo = useSelector((state) => {
    return state.GET_USER_INFO.data;
  });
  const [, getUserInfo] = useUserInfo();
  const [, resetLeadGeneration] = resetLeadGenerationApi();

  const getStepFromPage = (page) => {
    const stepMapping = {
      1: 1,
      2: 1,
      3: 2,
      4: 3,
      5: 5,
    };
    return stepMapping[page] || null;
  };

  const consultantData = useSelector((state) => {
    return state.GET_CALANDAR_TIMES.data;
  });

  const getConsultantName = (consultantId) => {
    if (consultantData && Object.keys(consultantData).length > 0) {
      const { consultants } = consultantData;
      const consultant = consultants.find((el) => {
        return el.consultantId === consultantId;
      });

      return `${consultant.firstName} ${consultant.lastNameInitials}`;
    }

    return consultantId;
  };

  useEffect(() => {
    if (userInfo?.uuid === '') {
      getUserInfo();
    }

    if (userDSInfo?.uuid === '') {
      getDSUserInfo({ userInfo });
    }
  }, []);
  // for pageload event tracking, not needed we can remove
  useEffect(() => {
    if (!dsUserInfo.loading && dsUserInfo.data.uuid !== '' && userInfo?.uuid) {
      event('NEW_PAGE_LOAD', {
        pageName: `apimarketplace:welcome:intake`,
        siteSectionL1: 'welcome',
        siteSectionL2: `intake`,
        dsUserInfo: dsUserInfo.data,
        userInfo,
      });
    }
  }, [dsUserInfo, userInfo]);

  // for return without submit event tracking
  useEffect(() => {
    return () => {
      if (
        !submitLeadingGenApi.data ||
        !Object.keys(submitLeadingGenApi.data).length
      ) {
        event('JOB_CANCEL', {
          event,
          jobName,
        });
      }

      resetLeadGeneration();
    };
  }, []);

  const renderStepContent = (currentPage) => {
    switch (currentPage) {
      case 1:
        return (
          <div>
            <FirstPart />
          </div>
        );
      case 2:
        return (
          <div>
            <OrgStepTwo />
          </div>
        );
      case 3:
        return (
          <div>
            <ThirdPart />
          </div>
        );
      case 4:
        return (
          <div>
            <IntegrateAPI />
          </div>
        );
      case 5:
        return (
          <div>
            <Completed />
          </div>
        );
      default:
        return null; // Fallback if no valid page
    }
  };

  const fireClickEvent = (currentPage) => {
    if (currentPage !== 2 && currentPage !== 3) return;
    const { orgInfoPartOne, orgInfoPartTwo } = form.getValues();
    const requestedAPI = orgInfoPartTwo.selectedAPIs.join();
    const organizationName = orgInfoPartOne.orgName;
    const organizationType = orgInfoPartOne.orgType;
    const supportedRcmServices =
      orgInfoPartTwo.rcmSupportedServices?.join(', ') || '';

    const payload = {
      event,
      jobName: 'request a meeting with an api consultant',
      organizationName,
      fullName: `${userInfo.firstName} ${
        userInfo.lastName || userInfo.family_name
      }`,
      organizationType,
      supportedRcmServices,
      emrSystem: orgInfoPartTwo.emrVendor || '',
      organizationWebsite: orgInfoPartOne.orgWebsite,
      emailAddress: orgInfoPartOne.email,
      phoneNumber: orgInfoPartOne.phone || '',
      requestedAPI,
      hasITResources: orgInfoPartTwo.isItResourceAvailable || '',
      itResourceType: orgInfoPartTwo.itResourceType || '',
      outsourcedCompanyName: orgInfoPartTwo.itCompanyName || '',
      apiWorkflowAutomationTimeline: orgInfoPartTwo.timeline || '',
      numberOfTaxIds: orgInfoPartOne.tinCount || '',
      providerTaxId: orgInfoPartOne.primaryTaxId || '',
      useOfAutomatedTransactions: orgInfoPartTwo.automations || '',
      isDesignatedGroup: '',
      businessValue: '',
      ohid: userInfo.username,
    };

    if (currentPage === 3) {
      payload.readyToConnectApi = '';
      event('JOB_REQUEST_API_SUBSCRIPTION_SUBMIT_INTAKE', payload);
    } else {
      event('JOB_SUBMIT_ORGANIZATION_INFORMATION', payload);
    }
  };

  const getInteractionName = (currentPage) => {
    const interactionMap = {
      1: 'Organization Information Part One',
      2: 'Organization Information Part Two',
      3: 'Recommended APIs',
      4: 'Connect to API',
    };
    return interactionMap[currentPage] || 'Schedule a meeting';
  };

  const getPageFields = (currentPage) => {
    const pageFields = {
      1: 'orgInfoPartOne',
      2: 'orgInfoPartTwo',
      4: 'connectToAPI',
      5: 'meeting',
    };
    return pageFields[currentPage];
  };

  const handleNextClick = () => {
    const { currentPage } = paginationProps.state;
    const connectToAPI = form.getValues('connectToAPI') === 'Yes';

    const interactionName = getInteractionName(currentPage);

    event('INTERACTION_EVENT', {
      event,
      interactionName,
      interactionValue: 'next',
      interactionContext: 'request a meeting with an api consultant',
    });

    // Button text and redirect logic
    if (currentPage === 4 && connectToAPI) {
      setButtonText('Go to API Marketplace registration');
    } else if (currentPage === 5 && connectToAPI) {
      window.location.href = config('DIGITAL_SECURITY_SSO_URL');
    } else {
      setButtonText('Next');
    }

    // Validation and navigation
    const fieldsToValidate = getPageFields(currentPage);
    if (!fieldsToValidate) {
      fireClickEvent(currentPage);
      paginationProps.nextPage();
    } else {
      form.validate(fieldsToValidate, () => {
        fireClickEvent(currentPage);
        paginationProps.nextPage();
      });
    }
  };

  const handlePreviousClick = () => {
    const connectToAPI = form.getValues('connectToAPI') === 'Yes';
    const { currentPage } = paginationProps.state;

    const interactionMap = {
      2: 'Organization Information Part Two',
      3: 'Recommended APIs',
      4: 'Connect with a consultant',
      5: connectToAPI
        ? 'Next Steps:Registration Link'
        : 'Next Steps:Schedule a meeting',
    };

    const interactionName =
      interactionMap[currentPage] || 'Next Steps:Schedule a meeting';

    event('INTERACTION_EVENT', {
      event,
      interactionName,
      interactionValue: 'back',
      interactionContext: 'request a meeting with an api consultant',
    });

    paginationProps.previousPage();
    setButtonText('Next');
  };

  // Function to fire job complete event
  const fireJobCompleteEvent = (data) => {
    const { orgInfoPartOne, orgInfoPartTwo } = data;
    const connectToAPI = form.getValues('connectToAPI') === 'Yes';
    const meeting = connectToAPI ? {} : data.meeting; // Use empty object if connectToAPI is "Yes"

    const requestedAPI = orgInfoPartTwo.selectedAPIs.join();
    const organizationName = orgInfoPartOne.orgName;
    const organizationType = orgInfoPartOne.orgType;

    const preferredMeetingDateTime = meeting.preferredMeetingDate
      ? `${meeting.preferredMeetingDate} ${meeting.preferredMeetingTime} EST`
      : '';

    const supportedRcmServices =
      orgInfoPartTwo.rcmSupportedServices?.join(', ') || '';

    event('JOB_REQUEST_API_SUBSCRIPTION_SUBMIT_INTAKE_COMPLETE', {
      event,
      jobName,
      organizationName,
      fullName: `${userInfo.firstName} ${
        userInfo.lastName || userInfo.family_name
      }`,
      organizationType,
      supportedRcmServices,
      emrSystem: orgInfoPartTwo.emrVendor || '',
      organizationWebsite: orgInfoPartOne.orgWebsite,
      emailAddress: orgInfoPartOne.email,
      phoneNumber: orgInfoPartOne.phone || '',
      requestedAPI,
      hasITResources: orgInfoPartTwo.isItResourceAvailable || '',
      itResourceType: orgInfoPartTwo.itResourceType || '',
      outsourcedCompanyName: orgInfoPartTwo.itCompanyName || '',
      apiWorkflowAutomationTimeline: orgInfoPartTwo.timeline || '',
      numberOfTaxIds: orgInfoPartOne.tinCount || '',
      providerTaxId: orgInfoPartOne.primaryTaxId || '',
      useOfAutomatedTransactions: orgInfoPartTwo.automations || '',
      preferredMeetingDateTime, // Empty if connectToAPI is "Yes"
      isDesignatedGroup: '',
      businessValue: '',
      preferredConsultant: connectToAPI
        ? ''
        : getConsultantName(meeting.consultant) || '', // Empty if connectToAPI is "Yes"
      readyToConnectApi: '',
      ohid: userInfo.username, // Added OHID to the payload
    });
  };

  const scheduleMeeting = (data) => {
    const { orgInfoPartOne, orgInfoPartTwo, meeting } = data;
    const scheduleMeetingRequest = {
      orgName: orgInfoPartOne.orgName,
      orgType: orgInfoPartOne.orgType,
      orgWebsite: orgInfoPartOne.orgWebsite || '',
      orgEmail: orgInfoPartOne.email,
      phoneNo: orgInfoPartOne.phone,
      tinCount: orgInfoPartOne.tinCount,
      primaryTaxId: orgInfoPartOne.primaryTaxId,
      emrVendor: orgInfoPartTwo.emrVendor || '',
      rcmSupportedServices: orgInfoPartTwo.rcmSupportedServices || [],
      isItResourceAvailable: orgInfoPartTwo.isItResourceAvailable,
      apis: orgInfoPartTwo.selectedAPIs || [],
      itResourceType: orgInfoPartTwo.itResourceType || '',
      itCompanyName: orgInfoPartTwo.itCompanyName || '',
      timeline: orgInfoPartTwo.timeline,
      automations: orgInfoPartTwo.automations || '',
      preferredDate: meeting.preferredMeetingDate,
      preferredTime: meeting.preferredMeetingTime,
      preferredConsultantId: meeting.consultant,
      intake: true,
    };
    fireJobCompleteEvent(data);
    postLeadGenerationApi({
      variables: { requestObj: scheduleMeetingRequest },
      userInfo,
    });
  };

  useEffect(() => {
    if (
      !submitLeadingGenApi.loading &&
      !submitLeadingGenApi.error &&
      Object.keys(submitLeadingGenApi.data || {}).length
    ) {
      paginationProps.nextPage();
    }
  }, [submitLeadingGenApi]);

  return (
    <React.Fragment>
      <StepIndicator
        currentStep={getStepFromPage(paginationProps.state.currentPage)}
        variant="minimal"
      >
        <StepIndicator.Step label="Organization Information" />
        <StepIndicator.Step label="Recommended APIs" />
        <StepIndicator.Step label="Connect with a consultant" />
      </StepIndicator>
      <FormProvider state={form} onSubmit={scheduleMeeting}>
        <StepContent
          style={{
            backgroundColor: 'white',
            width: '100%',
            padding: '20px',
            marginTop: '30px',
          }}
        >
          {renderStepContent(paginationProps.state.currentPage)}
        </StepContent>

        {paginationProps.state.currentPage !== 6 && (
          <Layout.Group
            style={{
              marginTop: 10,
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <div>
              {paginationProps.canPreviousPage &&
              paginationProps.state.currentPage !== 7 ? (
                <Button
                  data-id="1"
                  onClick={handlePreviousClick}
                  variant="outline"
                  isDisabled={!paginationProps.canPreviousPage}
                  before={<IconSymbol icon="arrow_back_ios" size="20px" />}
                >
                  Back
                </Button>
              ) : null}
            </div>

            <div>
              {paginationProps.canNextPage &&
              paginationProps.state.currentPage !== 4 ? (
                <Button
                  data-id="2"
                  onClick={handleNextClick}
                  after={<IconSymbol icon="arrow_forward_ios" size="20px" />}
                >
                  {buttonText}
                </Button>
              ) : null}
              {paginationProps.state.currentPage === 4 && (
                <Button
                  data-id="4"
                  type="submit"
                  isLoading={submitLeadingGenApi.loading}
                  ariaLoadingLabel="Scheduling meeting"
                >
                  Submit
                </Button>
              )}
            </div>
          </Layout.Group>
        )}
      </FormProvider>
    </React.Fragment>
  );
};
